import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { GUIDES } from "../data/content";

export default function Guides() {
  const [featured, ...rest] = GUIDES;
  return (
    <>
      <SEO
        title="Informational Guides & Blog"
        description="Content-rich guides on maximizing travel club points, avoiding rising maintenance fees, mastering booking tiers, and getting the most from your membership."
        path="/guides"
      />

      {/* HERO */}
      <section className="relative flex min-h-[52vh] items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1516546453174-5e1098a4b4af?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Travel planning with a map, glasses and notes for membership guides"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 pt-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <Overline className="text-accent">Guides & Blog</Overline>
            <h1 className="mt-4 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Master your membership.
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-200">
              Step-by-step tutorials on using travel products, maximizing point systems,
              dodging fee creep, and booking the best tiers — written for real people.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-5 pt-16 lg:px-8">
        <Reveal>
          <Link
            to={`/guides/${featured.slug}`}
            data-testid={`guide-featured-${featured.slug}`}
            className="group grid overflow-hidden rounded-[2rem] card-3d lg:grid-cols-2"
          >
            <div className="relative min-h-[300px] overflow-hidden">
              <img
                src={featured.image}
                alt={featured.alt}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col justify-center p-8 sm:p-12">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy">
                {featured.category}
              </span>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy">
                {featured.title}
              </h2>
              <p className="mt-3 leading-relaxed text-slate-600">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-primary">
                Read the guide
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </div>
            </div>
          </Link>
        </Reveal>
      </section>

      {/* GRID */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-8 md:grid-cols-3">
          {rest.map((g, i) => (
            <Reveal key={g.slug} delay={i * 0.08}>
              <Link
                to={`/guides/${g.slug}`}
                data-testid={`guide-card-${g.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl card-3d"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={g.image}
                    alt={g.alt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-navy backdrop-blur">
                    {g.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-xl font-bold text-navy">{g.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">
                    {g.excerpt}
                  </p>
                  <div className="mt-5 flex items-center gap-1.5 text-xs font-medium text-slate-400">
                    <Clock className="h-3.5 w-3.5" /> {g.readTime}
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
